import React, { useState, useEffect, useContext } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthContext } from '../../contextStore/AuthContext';
import { ToastContext } from '../../contextStore/ToastContext';
import { productsRef, getProductRef } from '../../firebase/collections';
import { serverTimestamp } from '../../firebase/collections/fieldValues';
import { isAdExpired } from '../../utils/adExpiry';
import PostCards from '../PostCards/PostCards';
import BarLoading from '../Loading/BarLoading';
import './Dashboard.css';

const PENDING_DURATION_MS = 5 * 60 * 1000; // 5 minutes

function getCreatedDate(product) {
  if (!product?.createdAt) return null;
  return product.createdAt.toDate
    ? product.createdAt.toDate()
    : new Date(product.createdAt);
}

function isPendingExpired(product) {
  const created = getCreatedDate(product);
  if (!created) return true;
  return Date.now() - created.getTime() > PENDING_DURATION_MS;
}

function MyAds() {
  const { t } = useTranslation();
  const { user } = useContext(AuthContext);
  const { addToast } = useContext(ToastContext) || {};
  const history = useHistory();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');
  const [deletingId, setDeletingId] = useState(null);
  const [publishingId, setPublishingId] = useState(null);

  useEffect(() => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    const q = productsRef()
      .where('userId', '==', user.uid)
      .orderBy('createdAt', 'desc');
    q.get().then((snapshot) => {
      const list = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Auto-approve ads that have been pending for more than 5 minutes
      list.forEach((product) => {
        if (
          product.moderationStatus === 'pending' &&
          isPendingExpired(product)
        ) {
          getProductRef(product.id)
            .update({ moderationStatus: 'approved', updatedAt: serverTimestamp() })
            .catch(() => {});
          product.moderationStatus = 'approved';
        }
      });
      setPosts(list);
      setLoading(false);
    });
  }, [user?.uid]);

  const draftPosts = posts.filter((p) => p.status === 'draft');
  const activePosts = posts.filter(
    (p) => (!p.status || p.status === 'active') && !isAdExpired(p)
  );
  const soldPosts = posts.filter((p) => p.status === 'sold');
  const expiredPosts = posts.filter(
    (p) => p.status === 'expired' || (p.status === 'active' && isAdExpired(p))
  );

  const handleDeleteDraft = (productId) => {
    if (deletingId) return;
    setDeletingId(productId);
    getProductRef(productId)
      .delete()
      .then(() => {
        setPosts((prev) => prev.filter((p) => p.id !== productId));
        addToast?.(t('dashboard.draftDeleted'), 'success');
      })
      .catch(() => {
        addToast?.(t('dashboard.failedToDeleteDraft'), 'error');
      })
      .finally(() => setDeletingId(null));
  };

  const handleEditDraft = (product) => {
    history.push(`/ad/${product.id}/edit`);
  };

  const handlePublishDraft = (product) => {
    if (publishingId) return;
    // Validate minimum required fields before publishing
    if (!product.name?.trim()) {
      addToast?.(t('dashboard.pleaseAddTitle'), 'error');
      return;
    }
    if (!product.price && product.price !== 0) {
      addToast?.(t('dashboard.pleaseAddPrice'), 'error');
      return;
    }
    const hasImages = (product.images?.length > 0) || product.url;
    if (!hasImages) {
      addToast?.(t('dashboard.pleaseAddAtLeastOneImage'), 'error');
      return;
    }
    setPublishingId(product.id);
    getProductRef(product.id)
      .update({
        status: 'active',
        moderationStatus: 'pending',
        publishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      })
      .then(() => {
        setPosts((prev) =>
          prev.map((p) =>
            p.id === product.id
              ? { ...p, status: 'active', moderationStatus: 'pending', publishedAt: new Date() }
              : p
          )
        );
        addToast?.(t('dashboard.adPublishedSuccessfully'), 'success');
      })
      .catch(() => {
        addToast?.(t('dashboard.failedToPublishAd'), 'error');
      })
      .finally(() => setPublishingId(null));
  };

  const displayList =
    activeTab === 'drafts'
      ? draftPosts
      : activeTab === 'active'
        ? activePosts
        : activeTab === 'sold'
          ? soldPosts
          : expiredPosts;

  return (
    <div>
      <div className="dashboardTabs dashboardTabsSub">
        <button
          type="button"
          className={`dashboardTab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          {t('dashboard.active')} ({activePosts.length})
        </button>
        <button
          type="button"
          className={`dashboardTab ${activeTab === 'drafts' ? 'active' : ''}`}
          onClick={() => setActiveTab('drafts')}
        >
          {t('dashboard.drafts')} ({draftPosts.length})
        </button>
        <button
          type="button"
          className={`dashboardTab ${activeTab === 'sold' ? 'active' : ''}`}
          onClick={() => setActiveTab('sold')}
        >
          {t('dashboard.sold')} ({soldPosts.length})
        </button>
        <button
          type="button"
          className={`dashboardTab ${activeTab === 'expired' ? 'active' : ''}`}
          onClick={() => setActiveTab('expired')}
        >
          {t('dashboard.expired')} ({expiredPosts.length})
        </button>
      </div>
      {loading ? (
        <BarLoading />
      ) : displayList.length === 0 ? (
        <div className="emptyState">
          <p>
            {activeTab === 'active'
              ? t('dashboard.noActiveAds')
              : activeTab === 'drafts'
                ? t('dashboard.noDraftsSaved')
                : activeTab === 'sold'
                  ? t('dashboard.noSoldAds')
                  : t('dashboard.noExpiredAds')}
          </p>
        </div>
      ) : (
        <div className="myAdsGrid">
          {displayList.map((product, index) => (
            <div
              className="dashboardCard myAdsCardWrap"
              key={product.id || index}
            >
              <PostCards product={product} index={index} />
              <div className="myAdsCardFooter">
                {product.stats && (product.stats.views > 0 || product.stats.favorites > 0) && (
                  <div className="myAdsCardStats">
                    {product.stats.views > 0 && <span>{t('dashboard.adViews', { count: product.stats.views })}</span>}
                    {product.stats.favorites > 0 && <span>{t('dashboard.adFavorites', { count: product.stats.favorites })}</span>}
                  </div>
                )}
                {product.moderationStatus === 'pending' && !isPendingExpired(product) && (
                  <div className="myAdsStatusBar myAdsStatusBar--pending">
                    <span className="myAdsStatusDot"></span> {t('dashboard.reviewingYourAd')}
                  </div>
                )}
                {product.moderationStatus === 'rejected' && (
                  <div className="myAdsStatusBar myAdsStatusBar--rejected">
                    <span className="myAdsStatusDot"></span> {t('dashboard.adRejected')}
                  </div>
                )}
                {product.moderationStatus === 'flagged' && (
                  <div className="myAdsStatusBar myAdsStatusBar--flagged">
                    <span className="myAdsStatusDot"></span> {t('dashboard.flaggedForReview')}
                  </div>
                )}
                {activeTab === 'active' && (
                  <div className="myAdsCardActions">
                    {product.featuredRequestStatus === 'approved' ? (
                      <div className="myAdsFeaturedApproved">
                        <span className="myAdsFeaturedStar">&#9733;</span> {t('dashboard.featured')}
                      </div>
                    ) : product.featuredRequestStatus === 'requested' ? (
                      <div className="myAdsFeaturedPendingBar">
                        <span className="myAdsFeaturedClock">&#9203;</span> {t('dashboard.awaitingApproval')}
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="myAdsRequestFeaturedBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          getProductRef(product.id)
                            .update({
                              featuredRequestStatus: 'requested',
                              featuredRequestedAt: serverTimestamp(),
                              updatedAt: serverTimestamp(),
                            })
                            .then(() => {
                              setPosts((prev) =>
                                prev.map((p) =>
                                  p.id === product.id
                                    ? { ...p, featuredRequestStatus: 'requested', featuredRequestedAt: new Date() }
                                    : p
                                )
                              );
                              addToast?.(t('dashboard.featuredRequestSubmitted'), 'success');
                            })
                            .catch(() => addToast?.(t('dashboard.failedToRequestFeatured'), 'error'));
                        }}
                      >
                        {t('dashboard.requestFeatured')}
                      </button>
                    )}
                  </div>
                )}
                {activeTab === 'drafts' && (
                  <div className="myAdsDraftActions">
                    <button
                      type="button"
                      className="myAdsPublishDraftBtn"
                      disabled={publishingId === product.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePublishDraft(product);
                      }}
                    >
                      {publishingId === product.id ? t('dashboard.publishing') : t('dashboard.publish')}
                    </button>
                    <div className="myAdsDraftSecondaryActions">
                      <button
                        type="button"
                        className="myAdsEditDraftBtn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditDraft(product);
                        }}
                      >
                        {t('common.edit')}
                      </button>
                      <button
                        type="button"
                        className="myAdsDeleteDraftBtn"
                        disabled={deletingId === product.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteDraft(product.id);
                        }}
                      >
                        {deletingId === product.id ? t('settings.deleting') : t('common.delete')}
                      </button>
                    </div>
                  </div>
                )}
                {activeTab === 'expired' && (
                  <div className="myAdsCardActions">
                    <Link
                      to={{ pathname: '/create', state: { repost: product } }}
                      className="myAdsRepostBtn"
                    >
                      {t('dashboard.repost')}
                    </Link>
                  </div>
                )}
              </div>
              {activeTab === 'drafts' && (
                <span className="myAdsDraftBadge">{t('dashboard.draft')}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default MyAds;
